import { inject, Injectable, OnDestroy } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import {
  ClinicService,
  EstimateBalanceWorkflowStatus,
  isInsuranceRelevantToAnyAccount,
  isPerVisitInsuranceSelectionEnabled,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceType,
  resolveSelectedInsurance,
  selectableInsurances,
} from '@mmx/shared'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators'

import { EstimateBalanceFlowService } from './estimate-balance-flow.service'
import { EstimateBalanceStateService } from './estimate-balance-state.service'

// Coalesces near-simultaneous triggers (common on hard refresh) so an unscoped resolve()
// can't flash before the next trigger corrects it.
export const RESOLVE_DEBOUNCE_MS = 50

// Mirrors active coverages + selected P/S into balance state so the save payload carries
// `insurances` instead of defaulting to selfpay. Also derives the secondary-on-file gate and
// seeds new-estimate secondary defaults from coverage (see resolve()). Two triggers feed resolve():
//   - patientData.insurances$ — option universe, re-runs selection.
//   - state.estimateChanged$ — fires on hydrate/save/modify, deduped on (P,S) so
//     unrelated form edits don't retrigger; also handles insurances$-before-hydrate.
// All paths go through resolveSelectedInsurance so operator picks stay sticky and
// ambiguous slots force an explicit choice.
@Injectable()
export class EstimateInsuranceLoader implements OnDestroy {
  private readonly patientData = inject(PatientDataService)
  private readonly state = inject(EstimateBalanceStateService)
  private readonly flow = inject(EstimateBalanceFlowService)
  private readonly clinicService = inject(ClinicService)
  private readonly destroy$ = new Subject<void>()
  private attached = false

  // Bridged at constructor time so attach() (called later, from ngOnInit) can subscribe to it.
  private readonly appointments$ = toObservable(this.state.appointments)

  // Each trigger below only requests a resolve via this subject; debounced in attach().
  private readonly resolveRequested$ = new Subject<void>()

  // Cached so estimateChanged$-driven resolves don't refetch the option universe.
  private latestInsurances: PatientInsurance[] | null = null

  attach(): void {
    // Idempotent — a second attach() would double-write selection/options on every emit.
    if (this.attached) {
      return
    }
    this.attached = true
    this.resolveRequested$
      .pipe(debounceTime(RESOLVE_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.latestInsurances == null) {
          // Patient data not loaded — resolving would write an empty option set; let insurances$ catch up.
          return
        }
        this.resolve()
      })
    this.patientData.insurances$
      .pipe(takeUntil(this.destroy$))
      .subscribe(insurances => {
        this.latestInsurances = insurances
        this.resolveRequested$.next()
      })
    this.state.estimateChanged$
      .pipe(
        map(estimate => ({
          p: estimate?.insurances?.P?.insuranceId ?? null,
          s: estimate?.insurances?.S?.insuranceId ?? null,
          // Account scoping depends on selected appointments too, not just P/S ids.
          appointmentIds: (estimate?.appointmentIds ?? []).join(','),
        })),
        distinctUntilChanged((a, b) => a.p === b.p && a.s === b.s && a.appointmentIds === b.appointmentIds),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.resolveRequested$.next())
    this.appointments$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resolveRequested$.next())
    // ClinicService.clinic is a static populated by clinic$'s own HTTP call, which can race
    // insurances$/estimateChanged$ on a hard reload; re-resolve once it actually delivers.
    this.clinicService.clinic$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resolveRequested$.next())
  }

  private resolve(): void {
    const estimate = this.state.estimate()
    const patientId = estimate?.patientId ?? this.state.patientId()
    const insurances = this.patientScopedInsurances(this.latestInsurances ?? [], patientId)
    const accountIds = this.relevantAccountIds()
    const scopedInsurances = this.scopeToAccounts(insurances, accountIds)
    const getType = this.accountAwareTypeAccessor(accountIds)
    const preferredP = estimate?.insurances?.P?.insuranceId ?? null
    const preferredS = estimate?.insurances?.S?.insuranceId ?? null

    const primaryOptions = selectableInsurances(scopedInsurances, PatientInsuranceType.PRIMARY, getType)
    const secondaryOptions = selectableInsurances(scopedInsurances, PatientInsuranceType.SECONDARY, getType)
    const currentPrimary = this.state.primaryInsurance()
    const currentSecondary = this.state.secondaryInsurance()
    const nextPrimary = resolveSelectedInsurance(
      primaryOptions,
      currentPrimary,
      preferredP,
    )
    const nextSecondary = resolveSelectedInsurance(
      secondaryOptions,
      currentSecondary,
      preferredS,
    )
    this.state.setPrimaryInsuranceOptions(primaryOptions)
    this.state.setSecondaryInsuranceOptions(secondaryOptions)
    this.refreshBenefitsIfSelectionChanged('primary', currentPrimary, nextPrimary)
    this.refreshBenefitsIfSelectionChanged('secondary', currentSecondary, nextSecondary)
    this.state.setPrimaryInsurance(nextPrimary)
    this.state.setSecondaryInsurance(nextSecondary)
    const balance = this.state.balance()
    if (estimate?.id && (balance == null || balance.workflowStatus === EstimateBalanceWorkflowStatus.DRAFT)
      && (preferredP !== (nextPrimary?.id ?? null)
        || (estimate.inputs?.hasSecondary === true && preferredS !== (nextSecondary?.id ?? null)))) {
      this.state.markDirty()
    }

    // Options-menu "patient has secondary on file" gate. Set here (mirroring legacy EstimateService),
    // not by the in-panel toggle — that conflation kept it permanently false.
    const hasSecondaryOnFile = secondaryOptions.length > 0
    this.state.setHasSecondaryInsurance(hasSecondaryOnFile)
    this.seedNewEstimateSecondary(hasSecondaryOnFile)
  }

  private refreshBenefitsIfSelectionChanged(
    slot: 'primary' | 'secondary',
    previous: PatientInsurance | null,
    next: PatientInsurance | null,
  ): void {
    if (this.insuranceKey(previous) === this.insuranceKey(next)) {
      return
    }
    const op$ = slot === 'primary'
      ? this.flow.selectPrimaryInsurance(next)
      : this.flow.selectSecondaryInsurance(next)
    op$.pipe(takeUntil(this.destroy$)).subscribe({ error: () => undefined })
  }

  private patientScopedInsurances(insurances: PatientInsurance[], patientId: string | null): PatientInsurance[] {
    if (!patientId) {
      return insurances
    }
    return insurances.filter(i => !i.patientId || i.patientId === patientId)
  }

  /**
   * Accounts of this estimate's selected appointments, when the clinic resolves insurance
   * per visit — two Primaries on different accounts aren't a real conflict there.
   */
  private relevantAccountIds(): Set<string> | null {
    if (!isPerVisitInsuranceSelectionEnabled(this.clinicService.clinic)) {
      return null
    }
    const selectedIds = this.state.selectedAppointmentIds()
    if (selectedIds.length === 0) {
      return null
    }
    const accountIds = new Set(
      this.state.appointments()
        .filter(a => selectedIds.includes(a.id))
        .map(a => a.accountId)
        .filter((id): id is string => !!id),
    )
    return accountIds.size > 0 ? accountIds : null
  }

  private scopeToAccounts(insurances: PatientInsurance[], accountIds: Set<string> | null): PatientInsurance[] {
    return insurances.filter(i => isInsuranceRelevantToAnyAccount(i, accountIds))
  }

  /**
   * Resolves each insurance's role via PatientInsurance.getAccountType — the same resolution
   * insurances-card/appointment-eligibility-card use — so records shared across visits (e.g.
   * both marked PRIMARY at the patient level, disambiguated only by accountTypes) don't compete
   * as duplicate options within a single visit's estimate. Only well-defined for exactly one
   * relevant account; a combined multi-visit estimate falls back to the raw shared type, same as
   * scopeToAccounts' own "any account" scoping.
   */
  private accountAwareTypeAccessor(accountIds: Set<string> | null): (insurance: PatientInsurance) => PatientInsuranceType | undefined {
    const perVisitEnabled = isPerVisitInsuranceSelectionEnabled(this.clinicService.clinic)
    const singleAccountId = accountIds?.size === 1 ? [...accountIds][0] : undefined

    return insurance => insurance.getAccountType(singleAccountId, perVisitEnabled)
  }

  private insuranceKey(insurance: PatientInsurance | null): string {
    return insurance ? `${insurance.id}:${insurance.eligibilityId ?? ''}` : ''
  }

  // New (unsaved) estimates apply secondary by default so the section shows without a manual toggle
  // (legacy parity); persisted estimates keep their calculated inputs. Guarded on the flag so a later
  // operator toggle-off isn't re-seeded (that setEstimate keeps P/S → estimateChanged$ dedupes).
  private seedNewEstimateSecondary(hasSecondaryOnFile: boolean): void {
    const estimate = this.state.estimate()
    if (!estimate?.inputs || estimate.id) {
      return
    }
    if ((estimate.inputs.hasSecondary ?? false) === hasSecondaryOnFile) {
      return
    }
    this.state.setEstimate({
      ...estimate,
      inputs: {
        ...estimate.inputs,
        hasSecondary: hasSecondaryOnFile,
        secondaryInsuranceCoversCoinsurance: hasSecondaryOnFile,
        secondaryInsuranceCoversCopayment: hasSecondaryOnFile,
        secondaryInsuranceCoversDeductible: hasSecondaryOnFile,
      },
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
